// The tick clock (protocol §8.1–8.2, movement.md §4.5): maps FRAME arrivals to a smoothed,
// delayed, fractional render tick that advances at the server's tick rate as measured by the
// local clock.
// - Tick period: `dtEstMs` = the least-squares slope of arrival time against tick over the last
//   50 frames (INTERPOLATION.jitterWindowFrames), clamped to ±10 % of the nominal `dtMs`, with
//   frames more than 3σ (and 15 ms) late left out of a second pass. Until 10 frames spanning 10
//   ticks are in, it is the nominal period. Frames more than LATE_JUMP_MS late are left out
//   (a time jump is a step, not a rate). This absorbs a client/server clock-rate mismatch
//   (the WSL machine's performance.now runs ~4.5 % fast): with a fixed period, render time
//   drifts ahead of the data and motion stair-steps at 25 Hz.
// - Baseline: a line of slope `dtEstMs` through (`baseTick`, `baseMs`), rebased to each FRAME's
//   tick so a new slope never shifts the line at the newest tick. Each FRAME (tick T at local
//   `t`): `dev = t − (baseMs + (T − baseTick)·dtEstMs)`; a negative `dev` drops the baseline to
//   `t` at once, otherwise it rises by 1 % of `dev` (INTERPOLATION.offsetDriftPerFrame).
//   `offsetMs` = the baseline extended back to tick 0.
// - `jitter` = standard deviation of `t − baseline` over the last 50 frames.
// - `delay` targets `clamp(1.5·dtEstMs + 2·jitter, 60, 160)` and moves ≤ 2 ms per `renderTick`.
// - `rt = baseTick + (now − baseMs − delay) / dtEstMs`.
// - Ahead re-seed: when `rt > lastTick + 0.5` on 10 `renderTick` calls in a row, the baseline is
//   re-seeded to the newest FRAME (render time can never stay ahead of the data).
// - Time jump: when every frame for `LATE_STREAK_FRAMES` in a row arrives more than
//   `LATE_JUMP_MS` after the baseline, the baseline is re-seeded to the earliest of those. That
//   happens when the server drops missed time after a long stall (mechanics §2) or the route's
//   latency grows for good.
// - A FRAME whose tick is lower than the last one starts a new timeline (`reset()` first).
// - Each FRAME also writes the shared `FrameTimeline` (newest tick, ms per tick) that sample
//   histories use to decide when to extrapolate (game/interpolate.ts).
//
// Implementer: client net/game. Consumers: net/handlers.ts (`onFrame`), app.ts main loop
// (`renderTick`, `isStalled` for the "Laggy connection" chip, `lastFrameAtMs` for steering).
// DOM-free; times are `performance.now()` ms passed in.

import { clamp, INTERPOLATION, NET_TIMING } from '@ballio/shared';
import { FRAME_TIMELINE, type FrameTimeline } from './interpolate.js';

/** The delay before the first `renderTick` call (about two ticks plus typical jitter). */
export const INITIAL_DELAY_MS = 100;

/** A frame this much later than the baseline counts toward a time jump. */
export const LATE_JUMP_MS = 250;

/** Consecutive late frames that make a time jump (1 s at 25 Hz). */
export const LATE_STREAK_FRAMES = 25;

/** Interpolation delay floor (movement.md §4.5: 80 → 60 once rate estimation and extrapolation are in). */
export const DELAY_MIN_MS = INTERPOLATION.delayMinMs;

/** The delay target is this many tick periods plus twice the jitter (movement.md §4.5). */
export const DELAY_TICKS = 1.5;

/** The estimated tick period stays within ±10 % of nominal. */
export const RATE_CLAMP = 0.1;

/** Frames (and tick span) needed before the slope replaces the nominal period. */
export const RATE_MIN_FRAMES = 10;

/** `rt` this far past the newest tick, on `AHEAD_RESEED_FRAMES` render calls in a row, re-seeds. */
export const AHEAD_RESEED_TICKS = 0.5;
export const AHEAD_RESEED_FRAMES = 10;

const WINDOW = INTERPOLATION.jitterWindowFrames;

/** Second fit pass: frames later than max(3σ, this) above the first fit are left out. */
const OUTLIER_MIN_MS = 15;

export class TickClock {
  /** Nominal ms per server tick (1000 / tickRate). */
  dtMs: number;
  /** Estimated ms of local time per server tick. */
  dtEstMs: number;
  jitterMs = 0;
  /** The current (smoothed) interpolation delay. */
  delayMs = INITIAL_DELAY_MS;
  /** Local time of the last FRAME (−Infinity before the first). */
  lastFrameAtMs = Number.NEGATIVE_INFINITY;
  /** Tick of the last FRAME (−1 before the first). */
  lastTick = -1;
  /** The shared FRAME timeline this clock writes (game/interpolate.ts). */
  readonly timeline: FrameTimeline;

  /** Baseline: local ms at which `baseTick` would arrive with the least delay seen. */
  private baseTick = 0;
  private baseMs = 0;
  /** `t − baseline` of the last WINDOW frames (a ring). */
  private readonly devs = new Float64Array(WINDOW);
  private devHead = 0;
  private devCount = 0;
  /** Ticks and arrival times of the last WINDOW frames (a ring, for the slope). */
  private readonly fitTick = new Float64Array(WINDOW);
  private readonly fitAt = new Float64Array(WINDOW);
  private fitHead = 0;
  private fitCount = 0;
  private hasOffset = false;
  private lateStreak = 0;
  private lateStreakMinDev = Number.POSITIVE_INFINITY;
  /** The baseline line at the first frame of the late streak. */
  private streakTick = 0;
  private streakMs = 0;
  private aheadStreak = 0;
  /** Scratch for `residual`. */
  private readonly res = { r: 0 };

  constructor(tickRate = 25, timeline: FrameTimeline = FRAME_TIMELINE) {
    this.dtMs = tickRate > 0 && Number.isFinite(tickRate) ? 1000 / tickRate : 40;
    this.dtEstMs = this.dtMs;
    this.timeline = timeline;
  }

  /** True once a FRAME has been seen since the last reset. */
  get hasFrames(): boolean {
    return this.hasOffset;
  }

  /** The baseline extended back to tick 0: `sample = t − T·dtEstMs` (protocol §8.2). */
  get offsetMs(): number {
    return this.hasOffset ? this.baseMs - this.baseTick * this.dtEstMs : 0;
  }

  /** The delay `renderTick` slews toward: `clamp(1.5·dtEstMs + 2·jitter, 60, 160)` ms. */
  get targetDelayMs(): number {
    return clamp(
      DELAY_TICKS * this.dtEstMs + 2 * this.jitterMs,
      DELAY_MIN_MS,
      INTERPOLATION.delayMaxMs,
    );
  }

  /** From WELCOME. Also resets. */
  setTickRate(tickRate: number): void {
    if (tickRate > 0 && Number.isFinite(tickRate)) this.dtMs = 1000 / tickRate;
    this.reset();
  }

  /** Forget all samples (new socket). */
  reset(): void {
    this.dtEstMs = this.dtMs;
    this.baseTick = 0;
    this.baseMs = 0;
    this.jitterMs = 0;
    this.delayMs = INITIAL_DELAY_MS;
    this.lastFrameAtMs = Number.NEGATIVE_INFINITY;
    this.lastTick = -1;
    this.forgetWindows();
    this.hasOffset = false;
    this.lateStreak = 0;
    this.lateStreakMinDev = Number.POSITIVE_INFINITY;
    this.aheadStreak = 0;
    this.timeline.newestTick = Number.NaN;
    this.timeline.dtMs = this.dtMs;
  }

  /** A FRAME with `tick` arrived at local time `atMs`. */
  onFrame(tick: number, atMs: number): void {
    if (!Number.isFinite(tick) || !Number.isFinite(atMs)) return;
    if (this.hasOffset && tick < this.lastTick) this.reset();
    let late = false;
    if (!this.hasOffset) {
      this.baseTick = tick;
      this.baseMs = atMs;
      this.hasOffset = true;
    } else {
      const predicted = this.baseMs + (tick - this.baseTick) * this.dtEstMs;
      const dev = atMs - predicted;
      this.baseTick = tick;
      this.baseMs = dev < 0 ? atMs : predicted + dev * INTERPOLATION.offsetDriftPerFrame;

      if (dev > LATE_JUMP_MS) {
        late = true;
        // Measured against the line as it was when the streak began (before the 1 % drifts).
        if (this.lateStreak === 0) {
          this.streakTick = tick;
          this.streakMs = predicted;
        }
        this.lateStreak++;
        const d = atMs - (this.streakMs + (tick - this.streakTick) * this.dtEstMs);
        if (d < this.lateStreakMinDev) this.lateStreakMinDev = d;
        if (this.lateStreak >= LATE_STREAK_FRAMES) {
          this.baseMs = this.streakMs + (tick - this.streakTick) * this.dtEstMs;
          this.baseMs += this.lateStreakMinDev;
          this.lateStreak = 0;
          this.lateStreakMinDev = Number.POSITIVE_INFINITY;
          // The windows measured the jump, not jitter or the rate.
          this.forgetWindows();
        }
      } else {
        this.lateStreak = 0;
        this.lateStreakMinDev = Number.POSITIVE_INFINITY;
      }
    }

    // A frame that may belong to a time jump stays out of the slope (a step would tilt it).
    if (!late) this.recordFit(tick, atMs);
    this.recordDeviation(atMs - this.baseMs);
    this.lastFrameAtMs = atMs;
    this.lastTick = tick;
    this.timeline.newestTick = tick;
    this.timeline.dtMs = this.dtMs;
  }

  /**
   * The fractional render tick for a display frame at `nowMs` (slews `delayMs`). Before the first
   * FRAME it returns `lastTick` (−1).
   */
  renderTick(nowMs: number): number {
    const target = this.targetDelayMs;
    const step = INTERPOLATION.delaySlewMsPerFrame;
    if (this.delayMs < target) this.delayMs = Math.min(target, this.delayMs + step);
    else if (this.delayMs > target) this.delayMs = Math.max(target, this.delayMs - step);
    if (!this.hasOffset) return this.lastTick;
    let rt = this.baseTick + (nowMs - this.baseMs - this.delayMs) / this.dtEstMs;
    if (rt > this.lastTick + AHEAD_RESEED_TICKS) {
      if (++this.aheadStreak >= AHEAD_RESEED_FRAMES) {
        this.aheadStreak = 0;
        const shift =
          this.lastFrameAtMs - (this.baseMs + (this.lastTick - this.baseTick) * this.dtEstMs);
        this.baseTick = this.lastTick;
        this.baseMs = this.lastFrameAtMs;
        // A jump (not rate drift): the fit window would tilt across it.
        if (Math.abs(shift) > LATE_JUMP_MS) this.forgetWindows();
        rt = this.baseTick + (nowMs - this.baseMs - this.delayMs) / this.dtEstMs;
      }
    } else {
      this.aheadStreak = 0;
    }
    return rt;
  }

  /** No FRAME for more than NET_TIMING.stalledFrameMs (250 ms). False before the first FRAME. */
  isStalled(nowMs: number): boolean {
    return this.hasOffset && nowMs - this.lastFrameAtMs > NET_TIMING.stalledFrameMs;
  }

  // -------------------------------------------------------------------------------------------

  private forgetWindows(): void {
    this.devHead = 0;
    this.devCount = 0;
    this.fitHead = 0;
    this.fitCount = 0;
  }

  private recordFit(tick: number, atMs: number): void {
    this.fitTick[this.fitHead] = tick;
    this.fitAt[this.fitHead] = atMs;
    this.fitHead = (this.fitHead + 1) % WINDOW;
    if (this.fitCount < WINDOW) this.fitCount++;
    const est = this.fitSlope();
    if (est !== null) {
      const lo = this.dtMs * (1 - RATE_CLAMP);
      const hi = this.dtMs * (1 + RATE_CLAMP);
      this.dtEstMs = clamp(est, lo, hi);
    }
  }

  /** Least-squares ms per tick over the fit window (null while it is too short). */
  private fitSlope(): number | null {
    const n = this.fitCount;
    if (n < RATE_MIN_FRAMES) return null;
    // Relative to the newest frame, so large ticks and times keep their precision.
    const ref = (this.fitHead + WINDOW - 1) % WINDOW;
    const t0 = this.fitTick[ref] as number;
    const a0 = this.fitAt[ref] as number;
    const first = this.slope(t0, a0, Number.POSITIVE_INFINITY);
    if (first === null) return null;
    // Second pass without the late outliers (one delayed frame tilts a 50-frame fit by ~2 %).
    let sq = 0;
    for (let i = 0; i < n; i++) {
      const e = this.residual(i, t0, a0, first);
      sq += e.r * e.r;
    }
    const cut = Math.max(3 * Math.sqrt(sq / n), OUTLIER_MIN_MS);
    const second = this.slope(t0, a0, cut, first);
    return (second ?? first).b;
  }

  /** Fit `at − a0 = c + b·(tick − t0)` over the window, skipping residuals above `cut`. */
  private slope(
    t0: number,
    a0: number,
    cut: number,
    prev?: { readonly b: number; readonly c: number },
  ): { b: number; c: number } | null {
    let n = 0;
    let sx = 0;
    let sy = 0;
    let sxx = 0;
    let sxy = 0;
    for (let i = 0; i < this.fitCount; i++) {
      if (prev && this.residual(i, t0, a0, prev).r > cut) continue;
      const x = (this.fitTick[i] as number) - t0;
      const y = (this.fitAt[i] as number) - a0;
      n++;
      sx += x;
      sy += y;
      sxx += x * x;
      sxy += x * y;
    }
    if (n < RATE_MIN_FRAMES) return null;
    const vx = sxx - (sx * sx) / n;
    // The ticks must span at least RATE_MIN_FRAMES ticks' worth of variance.
    if (!(vx > (RATE_MIN_FRAMES * RATE_MIN_FRAMES) / 12)) return null;
    const b = (sxy - (sx * sy) / n) / vx;
    const c = (sy - b * sx) / n;
    return Number.isFinite(b) ? { b, c } : null;
  }

  private residual(
    i: number,
    t0: number,
    a0: number,
    fit: { readonly b: number; readonly c: number },
  ): { r: number } {
    const x = (this.fitTick[i] as number) - t0;
    const y = (this.fitAt[i] as number) - a0;
    this.res.r = y - (fit.c + fit.b * x);
    return this.res;
  }

  private recordDeviation(d: number): void {
    this.devs[this.devHead] = d;
    this.devHead = (this.devHead + 1) % WINDOW;
    if (this.devCount < WINDOW) this.devCount++;
    // Population standard deviation, two passes (50 values).
    const n = this.devCount;
    let sum = 0;
    for (let i = 0; i < n; i++) sum += this.devs[i] as number;
    const mean = sum / n;
    let sq = 0;
    for (let i = 0; i < n; i++) {
      const e = (this.devs[i] as number) - mean;
      sq += e * e;
    }
    this.jitterMs = Math.sqrt(sq / n);
  }
}
